<script setup lang="ts">
import { computed } from 'vue';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 select-none whitespace-nowrap',
    'font-medium transition-all duration-200 ease-apple',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
    'disabled:opacity-50 disabled:pointer-events-none',
    'active:scale-[0.98]',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-accent text-accent-fg shadow-soft',
          'hover:bg-accent-hover hover:shadow-lift',
        ],
        secondary: [
          'bg-surface text-text border border-border',
          'hover:bg-bg hover:border-border-strong',
        ],
        ghost: [
          'bg-transparent text-text',
          'hover:bg-surface',
        ],
        subtle: [
          'bg-surface text-text-muted',
          'hover:bg-bg hover:text-text',
        ],
        danger: [
          'bg-danger text-white shadow-soft',
          'hover:opacity-90',
        ],
        link: [
          'bg-transparent text-accent p-0 h-auto',
          'hover:underline',
        ],
      },
      size: {
        sm: 'h-8 px-3 text-[13px] rounded-md',
        md: 'h-10 px-4 text-[14px] rounded-lg',
        lg: 'h-12 px-6 text-[15px] rounded-lg',
        xl: 'h-14 px-8 text-[16px] rounded-xl',
        icon: 'h-10 w-10 rounded-lg',
        'icon-sm': 'h-8 w-8 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

type ButtonVariants = VariantProps<typeof buttonVariants>;

const props = defineProps<{
  variant?: ButtonVariants['variant'];
  size?: ButtonVariants['size'];
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean;
  as?: string;
  class?: string;
}>();

const tag = computed(() => props.as ?? 'button');
const classes = computed(() => cn(buttonVariants({ variant: props.variant, size: props.size }), props.class));
</script>

<template>
  <component
    :is="tag"
    :type="tag === 'button' ? (type ?? 'button') : undefined"
    :disabled="disabled || loading"
    :class="classes"
  >
    <span
      v-if="loading"
      class="h-4 w-4 rounded-full border-2 border-current border-r-transparent animate-spin"
      aria-hidden="true"
    />
    <slot />
  </component>
</template>
